import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { SellerBalanceById } from "@/components/SellerBalance";
import { PageHeader, StatusBadge, LoadingSkeleton, EmptyState } from "@/components/ui-system";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CourierSelect } from "@/pages/seller/b2c/CourierSelect";
import ManifestApiLogs from "@/components/ManifestApiLogs";
import { can } from "@/lib/permissions";
import ChannelBadge from "@/components/b2c/ChannelBadge";
import ShippingLabelDownload from "@/components/b2c/ShippingLabelDownload";
import { RateMath } from "@/components/b2c/RateMath";
import { B2COrderEditDialog } from "@/components/b2c/B2COrderEditDialog";
import { B2COrderEditHistory } from "@/components/b2c/B2COrderEditHistory";
import { toast } from "sonner";
import {
  User,
  Package,
  CreditCard,
  Receipt,
  Clock,
  AlertTriangle,
  MapPin,
  Truck,
  Building2,
  ExternalLink,
  RefreshCw,
  Loader2,
  Pencil,
} from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";

const inr = (n) =>
  n == null
    ? "-"
    : `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const errDetail = (error, fallback) => {
  const d = error?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return error?.response?.data?.message || fallback;
};

const Row = ({ label, value, mono }) => (
  <div className="flex items-start justify-between gap-3 py-1.5">
    <dt className="text-sm text-slate-500 shrink-0">{label}</dt>
    <dd className={`text-sm text-slate-800 text-right break-words ${mono ? "font-mono" : ""}`}>{value ?? "-"}</dd>
  </div>
);

const B2CAdminOrderDetail = ({ user, onLogout }) => {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCourierSelect, setShowCourierSelect] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const fetchOrder = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/b2c/admin/orders/${orderId}`);
      setOrder(data);
    } catch (e) {
      const msg = errDetail(e, "Could not load this order");
      setError(msg);
      if (e?.response?.status === 404) toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  // Pull the courier's latest status + a deferred AWB now (the "AWB_ASSIGNED but
  // no AWB yet" case) instead of waiting for the poll cron.
  const refreshTracking = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await axios.post(`${API}/b2c/admin/orders/${orderId}/refresh-tracking`);
      if (data.awb_stamped) toast.success(`AWB fetched from courier: ${data.awb}`);
      else if (data.advanced) toast.success("Status refreshed from courier");
      else if (data.awb) toast.success("Tracking refreshed — already up to date");
      else toast.info("Courier hasn't assigned an AWB yet — try again shortly");
      await fetchOrder();
    } catch (e) {
      toast.error(errDetail(e, "Could not refresh from courier"));
    } finally {
      setRefreshing(false);
    }
  }, [orderId, fetchOrder]);

  // Courier picker inputs for shipping a NEW order on the seller's behalf.
  const quoteInput = useMemo(() => {
    if (!order) return null;
    return {
      pickup_location_id: order.pickup_location_id || undefined,
      drop_pincode: order.buyer?.pincode,
      payment_mode: order.payment_mode,
      order_value: order.order_value,
      package: {
        dead_weight_kg: order.package?.dead_weight_kg,
        length_cm: order.package?.length_cm,
        breadth_cm: order.package?.breadth_cm,
        height_cm: order.package?.height_cm,
      },
    };
  }, [order]);
  const shipExtraBody = useMemo(() => ({ seller_id: order?.seller_id }), [order?.seller_id]);
  const canShip = can(user, "b2c.orders:ship");

  if (loading) {
    return (
      <AdminLayout user={user} onLogout={onLogout}>
        <div className="space-y-6">
          <LoadingSkeleton variant="detail" />
        </div>
      </AdminLayout>
    );
  }

  if (error || !order) {
    return (
      <AdminLayout user={user} onLogout={onLogout}>
        <div className="space-y-6">
          <PageHeader title="B2C Order" backTo={() => navigate("/admin/b2c/orders")} />
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              <EmptyState
                icon={AlertTriangle}
                title="Order not found"
                description={error || "This order doesn't exist."}
                action={
                  <Button type="button" variant="outline" onClick={() => navigate("/admin/b2c/orders")}>
                    Back to orders
                  </Button>
                }
              />
            </CardContent>
          </Card>
        </div>
      </AdminLayout>
    );
  }

  const history = Array.isArray(order.status_history) ? order.status_history : [];
  const products = Array.isArray(order.products) ? order.products : [];
  const sellerName = order.seller_brand_name || order.seller_id;
  const marginColor =
    order.margin == null ? "text-slate-400" : order.margin >= 0 ? "text-green-700" : "text-red-600";

  const lineValue = (p) => {
    const gross = Number(p?.unit_price ?? 0) * Number(p?.qty ?? 0);
    const net = gross - Number(p?.discount ?? 0);
    return net;
  };

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="max-w-5xl mx-auto space-y-6">
        <PageHeader
          title={order.order_number}
          subtitle={`Created ${formatToIST(order.created_at)}${order.reference_number ? ` · Ref: ${order.reference_number}` : ""}`}
          backTo={() => navigate("/admin/b2c/orders")}
          badge={
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={order.status} kind="b2c" size="lg" />
              {sellerName && (
                <Badge variant="outline" className="border-slate-200 text-slate-700">
                  <Building2 className="w-3.5 h-3.5 mr-1 text-slate-400" />
                  {sellerName}
                </Badge>
              )}
              {order.seller_id && <SellerBalanceById sellerId={order.seller_id} className="!mt-0" />}
              {order.channel_name && (
                <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2 py-1">
                  <ChannelBadge name={order.channel_name} logoUrl={order.channel_logo_url} />
                </span>
              )}
            </div>
          }
          actions={
            canShip ? (
              <div className="flex flex-wrap gap-2">
                {order.status === "NEW" && (
                  <>
                    <Button variant="outline" onClick={() => setShowEdit(true)} data-testid="admin-detail-edit-order">
                      <Pencil className="w-4 h-4 mr-2" /> Edit order
                    </Button>
                    <Button onClick={() => setShowCourierSelect((v) => !v)} data-testid="admin-detail-ship-now">
                      <Truck className="w-4 h-4 mr-2" /> {showCourierSelect ? "Hide couriers" : "Ship Now"}
                    </Button>
                  </>
                )}
                {order.status !== "NEW" && (
                  <Button variant="outline" onClick={refreshTracking} disabled={refreshing} data-testid="admin-detail-refresh">
                    {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    Refresh from courier
                  </Button>
                )}
              </div>
            ) : null
          }
        />

        {/* Auto-assign tried to ship this order at creation and failed (best-effort). */}
        {order.status === "NEW" && order.auto_ship_error && (
          <Card className="border-0 shadow-sm border-l-4 border-l-amber-400">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
                <div>
                  <p className="text-sm font-semibold text-slate-800">Auto-assign didn't ship this order</p>
                  <p className="text-sm text-slate-600" data-testid="admin-detail-autoship-error">{order.auto_ship_error}</p>
                  <p className="mt-1 text-xs text-slate-400">Fix the cause, then ship it with “Ship Now”. It stays NEW until then.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* AWB deferred by the courier (ExpressFly assigns it after manifest) — fetch it now. */}
        {order.status !== "NEW" && !order.awb && canShip && (
          <Card className="border-0 shadow-sm border-l-4 border-l-amber-400">
            <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
                <div>
                  <p className="text-sm font-semibold text-slate-800">AWB pending from courier</p>
                  <p className="text-sm text-slate-500">
                    The order is booked but the courier hasn't returned an AWB to us yet (it's assigned after manifest).
                    Fetch it now instead of waiting for the sync.
                  </p>
                </div>
              </div>
              <Button variant="outline" onClick={refreshTracking} disabled={refreshing} data-testid="admin-detail-fetch-awb">
                {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Fetch AWB
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Ship a NEW order on the seller's behalf. */}
        {order.status === "NEW" && showCourierSelect && canShip && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Truck className="w-5 h-5 text-brand" /> Choose a courier
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CourierSelect
                quoteInput={quoteInput}
                orderId={order.id}
                onShipped={() => {
                  setShowCourierSelect(false);
                  fetchOrder();
                }}
                apiBase="b2c/admin"
                extraBody={shipExtraBody}
              />
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: timeline */}
          <Card className="border-0 shadow-sm lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-brand" /> Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <p className="text-sm text-slate-500">No status updates yet.</p>
              ) : (
                <ol className="relative border-l border-slate-200 ml-2 space-y-5">
                  {history.map((h, i) => (
                    <li key={i} className="ml-4" data-testid={`admin-b2c-timeline-${i}`}>
                      <span
                        className={`absolute -left-1.5 mt-1 h-3 w-3 rounded-full ${
                          i === history.length - 1 ? "bg-brand" : "bg-slate-300"
                        }`}
                      />
                      <div className="flex items-center gap-2 flex-wrap">
                        <StatusBadge status={h.status} kind="b2c" size="sm" />
                        {h.note && <span className="text-xs text-slate-500">{h.note}</span>}
                      </div>
                      {(h.by || h.role) && (
                        <p className="mt-0.5 text-xs text-slate-500">
                          by {h.by || "—"}{h.role ? ` · ${h.role}` : ""}
                        </p>
                      )}
                      {h.at && <p className="mt-0.5 text-xs text-slate-400">{formatToIST(h.at)}</p>}
                    </li>
                  ))}
                </ol>
              )}

              {/* Full courier tracking (every scan from the aggregator), newest first */}
              {Array.isArray(order.tracking?.checkpoints) && order.tracking.checkpoints.length > 0 && (
                <div className="mt-5 border-t border-slate-100 pt-4">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Courier tracking{order.tracking?.status_raw ? ` · ${order.tracking.status_raw}` : ""}
                  </p>
                  <ol className="relative border-l border-slate-200 ml-2 space-y-4">
                    {[...order.tracking.checkpoints]
                      .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0))
                      .map((c, i) => (
                        <li key={i} className="ml-4">
                          <span className={`absolute -left-1.5 mt-1 h-2.5 w-2.5 rounded-full ${i === 0 ? "bg-brand" : "bg-slate-300"}`} />
                          <p className="text-sm text-slate-800">{c.status || c.remark || c.scan || "—"}</p>
                          {(c.location || c.scan) && (
                            <p className="text-xs text-slate-500">{c.location || ""}{c.scan ? `${c.location ? " · " : ""}${c.scan}` : ""}</p>
                          )}
                          {c.timestamp && <p className="text-xs text-slate-400">{formatToIST(c.timestamp)}</p>}
                        </li>
                      ))}
                  </ol>
                </div>
              )}

              <div className="mt-5 border-t border-slate-100 pt-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Edit history</p>
                <B2COrderEditHistory editHistory={order.edit_history} />
              </div>
            </CardContent>
          </Card>

          {/* Right: buyer / package / fulfilment / money / products */}
          <div className="lg:col-span-2 space-y-6">
            {/* Buyer */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5 text-brand" /> Buyer
                </CardTitle>
              </CardHeader>
              <CardContent>
                <dl>
                  <Row label="Name" value={order.buyer?.name} />
                  <Row label="Phone" value={order.buyer?.phone} mono />
                  {order.buyer?.alt_phone && <Row label="Alt phone" value={order.buyer.alt_phone} mono />}
                  {order.buyer?.email && <Row label="Email" value={order.buyer.email} />}
                  <Row
                    label="Address"
                    value={
                      <span className="inline-flex items-start gap-1 justify-end">
                        <MapPin className="w-3.5 h-3.5 mt-0.5 text-slate-400 shrink-0" />
                        <span>
                          {order.buyer?.address}
                          {order.buyer?.landmark ? `, ${order.buyer.landmark}` : ""}
                          {", "}
                          {[order.buyer?.city, order.buyer?.state, order.buyer?.pincode].filter(Boolean).join(", ")}
                        </span>
                      </span>
                    }
                  />
                </dl>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Package */}
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Package className="w-5 h-5 text-brand" /> Package
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <dl>
                    <Row label="Dead weight" value={`${order.package?.dead_weight_kg ?? "-"} kg`} />
                    <Row label="Volumetric" value={`${order.package?.volumetric_kg ?? "-"} kg`} />
                    <Row
                      label="Charged weight"
                      value={<span className="font-semibold">{order.package?.charged_weight_kg ?? "-"} kg</span>}
                    />
                    <Row
                      label="Dimensions"
                      value={`${order.package?.length_cm ?? "-"}×${order.package?.breadth_cm ?? "-"}×${
                        order.package?.height_cm ?? "-"
                      } cm`}
                    />
                    {order.zone && (
                      <Row label="Zone" value={<Badge variant="outline" className="border-slate-200">{order.zone}</Badge>} />
                    )}
                  </dl>
                </CardContent>
              </Card>

              {/* Fulfilment */}
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Truck className="w-5 h-5 text-brand" /> Fulfilment
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <dl>
                    <Row label="Courier" value={order.aggregator_courier_name || order.aggregator_courier_code || order.courier_name || order.courier_code || "-"} />

                    <Row
                      label="Mode"
                      value={order.courier_mode ? <span className="capitalize">{order.courier_mode}</span> : "-"}
                    />
                    <Row label="Aggregator" value={order.aggregator ? <span className="capitalize">{order.aggregator}</span> : "-"} />
                    {order.aggregator_partner_id && (
                      <Row label="Partner ID" value={order.aggregator_partner_id} mono />
                    )}
                    <Row
                      label="AWB"
                      mono={!!order.awb}
                      value={order.awb || ((order.status && !["NEW", "CANCELLED"].includes(order.status)) ? (
                        <span className="inline-flex items-center gap-1.5 text-amber-600">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Assignment in progress
                        </span>
                      ) : "-")}
                    />
                    {order.label_url && (
                      <Row
                        label="Label"
                        value={
                          <a
                            href={order.label_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-brand hover:underline"
                            data-testid="admin-b2c-label-link"
                          >
                            Open label <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        }
                      />
                    )}
                  </dl>
                  {order.awb && (
                    <ShippingLabelDownload
                      apiBase="b2c/admin"
                      orderId={order.id}
                      className="mt-4 pt-4 border-t border-slate-100"
                    />
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Products */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-brand" /> Items
                </CardTitle>
              </CardHeader>
              <CardContent>
                {products.length === 0 ? (
                  <p className="text-sm text-slate-500">No items recorded.</p>
                ) : (
                  <>
                    {/* Desktop table */}
                    <div className="hidden sm:block overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                            <th className="py-2 pr-3 text-left font-semibold">Item</th>
                            <th className="py-2 px-3 text-right font-semibold">Qty</th>
                            <th className="py-2 px-3 text-right font-semibold">Unit price</th>
                            <th className="py-2 pl-3 text-right font-semibold">Line value</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {products.map((p, i) => (
                            <tr key={i}>
                              <td className="py-2 pr-3">
                                <div className="font-medium text-slate-800">{p.name}</div>
                                {p.sku && <div className="text-xs text-slate-500">SKU {p.sku}</div>}
                              </td>
                              <td className="py-2 px-3 text-right tabular-nums text-slate-700">{p.qty}</td>
                              <td className="py-2 px-3 text-right tabular-nums text-slate-700">{inr(p.unit_price)}</td>
                              <td className="py-2 pl-3 text-right tabular-nums font-medium text-slate-800">
                                {inr(lineValue(p))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile stack */}
                    <div className="sm:hidden space-y-2">
                      {products.map((p, i) => (
                        <div key={i} className="border-b border-slate-100 pb-2 last:border-0 last:pb-0">
                          <p className="text-sm font-medium text-slate-800">{p.name}</p>
                          <p className="text-xs text-slate-500 tabular-nums">
                            {p.sku ? `SKU ${p.sku} · ` : ""}Qty {p.qty} × {inr(p.unit_price)} = {inr(lineValue(p))}
                          </p>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Money — admin view: seller charge + our cost + margin (the white-label inversion) */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-brand" /> Money
                </CardTitle>
              </CardHeader>
              <CardContent>
                <dl>
                  <Row label="Mode" value={order.payment_mode === "cod" ? "Cash on Delivery" : "Prepaid"} />
                  <Row label="Order value" value={inr(order.order_value)} />
                  {order.payment_mode === "cod" && (
                    <Row
                      label="COD to collect"
                      value={<span className="font-semibold text-amber-700">{inr(order.cod_amount)}</span>}
                    />
                  )}
                  <div className="my-2 border-t border-slate-100" />
                  {order.seller_rate ? (
                    <div className="py-1">
                      <RateMath rate={order.seller_rate} totalLabel="Seller charge" />
                    </div>
                  ) : (
                    <Row
                      label="Seller charge"
                      value={<span className="font-semibold text-slate-800 tabular-nums">{inr(order.seller_charge)}</span>}
                    />
                  )}
                  <Row
                    label="Our cost"
                    value={<span className="font-semibold text-slate-800 tabular-nums">{inr(order.cost_total)}</span>}
                  />
                  <div className="flex items-center justify-between border-t pt-2 mt-1">
                    <dt className="text-sm font-semibold text-slate-700">
                      Margin
                      {order.margin_pct != null && (
                        <span className="ml-1 text-xs font-normal text-slate-400">
                          ({Number(order.margin_pct).toLocaleString("en-IN")}%)
                        </span>
                      )}
                    </dt>
                    <dd className={`text-base font-bold tabular-nums ${marginColor}`}>{inr(order.margin)}</dd>
                  </div>
                </dl>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Courier (aggregator) API calls for this order — admin-only (exposes our cost basis) */}
        <ManifestApiLogs
          orderId={order.id}
          logsUrl={`${API}/api-logs/order/${order.id}/b2c`}
          title="Courier API Logs"
          description="Aggregator (ExpressFly / Shiprocket / iThink) API calls for this order — request & response, success and failure."
          emptyText="No courier API calls have been logged for this B2C order yet."
          emptyHint={null}
        />
      </div>

      <B2COrderEditDialog
        order={order}
        open={showEdit}
        onOpenChange={setShowEdit}
        saveUrl={`${API}/b2c/admin/orders/${orderId}`}
        pickupUrl={order?.seller_id ? `${API}/pickup-locations/seller/${order.seller_id}` : null}
        onSaved={(updated) => { if (updated) setOrder(updated); fetchOrder(); }}
      />
    </AdminLayout>
  );
};

export default B2CAdminOrderDetail;
